/**
 * The studio document, shaped for the page-context envelope.
 *
 * A studio dock used to tell Vee only *that* a document existed and *that* the
 * user had hand-edited it ("docSource: user-edited"), never the text of either.
 * Asked to revise without clobbering those edits, Vee could only say it
 * couldn't see them and ask the user to paste their own document back — which
 * is what shipped, and what this module fixes.
 *
 * Two versions travel, because they answer two different questions:
 *   current  — what is on screen right now. The backend's edit_document tool
 *              does exact find/replace, so its `find` strings must come from
 *              here or they match nothing.
 *   baseline — Vee's version from before the hand-edits. current − baseline IS
 *              the user's work; without it, "keep my edits" is a promise the
 *              model has no way to keep. Sent only when it actually differs, so
 *              an untouched document costs one copy, not two.
 *
 * MARKDOWN, not flattened text — the same call the wire history makes for chat
 * drafts (see wire.js). Minutes are mostly tables; flattening them makes
 * formatting instructions impossible to follow and headings impossible to cite.
 */
import { htmlToMarkdown } from '../utils/draftUtils';

// Matches the backend's MAX_DOC_TEXT_CHARS (contracts.js). Trimming here keeps
// the request small; the server re-clamps regardless, since it cannot trust a
// client-sent length.
export const MAX_PAGE_DOC_CHARS = 12000;

const toText = (html) => {
  const raw = String(html || '').trim();
  if (!raw) return '';
  try {
    return htmlToMarkdown(raw).trim();
  } catch {
    // htmlToMarkdown already falls back internally; this only catches a hard
    // throw. A document the model can't read beats a turn that never sends.
    return '';
  }
};

/**
 * @param {object} docState  { currentHtml, baselineHtml } from the studio
 * @returns {{current?: string, baseline?: string}|undefined}
 */
export const buildDocPair = (docState) => {
  if (!docState) return undefined;
  const current = toText(docState.currentHtml).slice(0, MAX_PAGE_DOC_CHARS);
  if (!current) return undefined; // nothing generated yet — nothing to ground
  const baseline = toText(docState.baselineHtml).slice(0, MAX_PAGE_DOC_CHARS);
  const out = { current };
  // Identical texts mean the user hasn't diverged: one copy, and no "preserve
  // their edits" rules for edits that don't exist.
  if (baseline && baseline !== current) out.baseline = baseline;
  return out;
};

export default buildDocPair;
